// components/AgeVerification.tsx
'use client';

import { useState } from 'react';
import { useClientSnapshot } from '@/hooks/useClientSnapshot';

const AGE_VERIFICATION_KEY = 'ageVerifiedAt';
const VERIFICATION_TTL_MS = 45 * 24 * 60 * 60 * 1000;

function needsAgeVerification(): boolean {
  let verifiedAt: string | null = null;
  try {
    verifiedAt = localStorage.getItem(AGE_VERIFICATION_KEY);
  } catch { /* Storage can be unavailable; ask again. */ }
  if (!verifiedAt) return true;

  const age = Date.now() - Number.parseInt(verifiedAt, 10);
  return !Number.isFinite(age) || age > VERIFICATION_TTL_MS;
}

export default function AgeVerification() {
  // Hidden on the server and during hydration; the stored verification decides afterwards.
  const needsVerification = useClientSnapshot(needsAgeVerification, false);
  const [verifiedNow, setVerifiedNow] = useState(false);
  const show = needsVerification && !verifiedNow;

  const handleVerify = () => {
    localStorage.setItem(AGE_VERIFICATION_KEY, Date.now().toString());
    setVerifiedNow(true);
  };

  const handleDeny = () => {
    window.location.href = 'https://www.google.com';
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/85 px-4 backdrop-blur-xs">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-verification-title"
        aria-describedby="age-verification-description"
        className="w-full max-w-[22rem] rounded-2xl border border-white/10 bg-white p-5 text-center shadow-[0_24px_80px_-24px_rgba(0,0,0,0.65)] sm:p-6"
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-wine-red text-lg font-semibold text-white shadow-xs shadow-wine-red/20">
          21+
        </div>
        <h2 id="age-verification-title" className="text-2xl font-semibold tracking-tight text-gray-950">
          Are you 21 or older?
        </h2>
        <p id="age-verification-description" className="mx-auto mt-3 max-w-[18rem] text-sm leading-6 text-gray-600">
          You must be of legal drinking age to shop Amster Wine. Valid ID is required at delivery or pickup.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-2.5 sm:gap-3">
          <button
            type="button"
            className="min-h-11 rounded-xl bg-wine-red px-3 text-sm font-semibold text-white shadow-xs shadow-wine-red/20 transition-colors duration-200 hover:bg-wine-red-dark focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-wine-red focus-visible:ring-offset-2"
            onClick={handleVerify}
          >
            Yes, enter
          </button>
          <button
            type="button"
            className="min-h-11 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
            onClick={handleDeny}
          >
            No, leave
          </button>
        </div>
      </section>
    </div>
  );
}
